'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import {
  TrendingUp, BarChart2, Search, Star, Activity,
  Calendar, Newspaper, GitCompare, Heart, ExternalLink,
} from 'lucide-react';
import { getPublicDonationSettings, type DonationSettings } from '@/lib/api-client';

const FEATURES = [
  { icon: TrendingUp,  title: 'Real-Time Quotes',      desc: 'Live stock and ETF prices with change data, volume, and key fundamentals.' },
  { icon: Activity,    title: 'Market Heatmap',         desc: 'Visualize the entire market at a glance with a color-coded sector heatmap.' },
  { icon: BarChart2,   title: 'Market Movers',          desc: 'Top gainers, losers, most active, pre-market, and after-hours movers.' },
  { icon: Search,      title: 'Stock Screener',         desc: 'Filter stocks by sector, market cap, P/E ratio, volume, and more.' },
  { icon: Star,        title: 'Watchlist',              desc: 'Track your favorite stocks and ETFs in a personalized watchlist.' },
  { icon: Calendar,    title: 'Earnings & IPO Calendar', desc: 'Stay ahead of upcoming earnings reports and IPO events.' },
  { icon: GitCompare,  title: 'Compare Tool',           desc: 'Side-by-side comparison of stocks and ETFs across key metrics.' },
  { icon: Newspaper,   title: 'News Feed',              desc: 'Curated financial news for individual tickers and the broad market.' },
];

export default function AboutPage() {
  const [donation, setDonation] = useState<DonationSettings | null>(null);

  useEffect(() => {
    getPublicDonationSettings().then(setDonation).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-surface text-white">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-card-bg">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-500/10 via-transparent to-indigo-500/10 pointer-events-none" />
        <div className="max-w-4xl mx-auto px-3 py-20 text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-sky-500/10 border border-sky-500/20 rounded-full px-4 py-1.5 text-sky-400 text-sm font-medium mb-6">
            <TrendingUp size={14} />
            Independent &amp; Open Research
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Building Better Stock Research —<br className="hidden md:block" />
            <span className="text-sky-400"> For Everyone</span>
          </h1>
          <p className="text-lg text-fg-muted max-w-2xl mx-auto leading-relaxed">
            StocksIQPro is a free, independent financial research platform built by a small team
            passionate about democratizing access to professional-grade market data and tools —
            without the steep price tags of institutional platforms.
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-3 py-16 space-y-20">

        {/* Why We Built This */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-6">Why We Built This</h2>
          <div className="bg-card-bg border border-default rounded-xl p-8 space-y-4 text-slate-300 leading-relaxed">
            <p>
              Retail investors have always faced an uneven playing field. The best research tools —
              real-time data, advanced screeners, sector heatmaps — were locked behind expensive
              subscriptions that cost hundreds of dollars a month. We think that&apos;s wrong.
            </p>
            <p>
              We&apos;re a small, independent team running on a limited budget. We don&apos;t have venture
              capital backing or enterprise sales teams. What we do have is a genuine belief that
              every investor, from a first-time buyer to a seasoned analyst, deserves access to
              quality market research tools.
            </p>
            <p>
              StocksIQPro was built from scratch to give you the tools that professionals use, free
              of charge. We keep the lights on through optional donations from our community and
              minimal, non-intrusive advertising.
            </p>
          </div>
        </section>

        {/* Features */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-8">What You Get</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="bg-card-bg border border-default rounded-xl p-5 flex gap-4 items-start"
              >
                <div className="w-9 h-9 rounded-lg bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                  <Icon size={17} className="text-sky-400" />
                </div>
                <div>
                  <div className="font-semibold text-white text-sm mb-1">{title}</div>
                  <div className="text-fg-muted text-sm leading-relaxed">{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Support Our Mission */}
        <section>
          <div className="bg-gradient-to-br from-card-bg to-surface border border-default rounded-xl p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-lg bg-rose-500/10 flex items-center justify-center">
                <Heart size={18} className="text-rose-400" />
              </div>
              <h2 className="text-2xl font-bold text-white">Support Our Mission</h2>
            </div>
            <p className="text-fg-muted mb-6 leading-relaxed max-w-xl">
              Running StocksIQPro isn&apos;t free — server costs, data feeds, and development time all
              add up. If you find value in the platform, consider making a small donation. Every
              contribution helps keep the site free and ad-light for everyone.
            </p>

            {donation?.enabled ? (
              <div className="flex flex-wrap gap-3">
                {donation.paypalLink && (
                  <a
                    href={donation.paypalLink.startsWith('http') ? donation.paypalLink : `https://${donation.paypalLink}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#003087] hover:bg-[#00257a] text-white font-semibold text-sm px-5 py-2.5 rounded-lg transition-colors"
                  >
                    <ExternalLink size={14} />
                    Donate via PayPal
                  </a>
                )}
                {donation.venmoHandle && (
                  <a
                    href={`https://venmo.com/${donation.venmoHandle.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#008CFF] hover:bg-[#0070cc] text-white font-semibold text-sm px-5 py-2.5 rounded-lg transition-colors"
                  >
                    <ExternalLink size={14} />
                    Donate via Venmo
                  </a>
                )}
                {donation.zelleEmail && (
                  <div className="inline-flex items-center gap-2 bg-[#6d1ed4] text-white font-semibold text-sm px-5 py-2.5 rounded-lg select-all">
                    Zelle: {donation.zelleEmail}
                  </div>
                )}
                {donation.message && (
                  <p className="w-full mt-2 text-fg-muted text-sm italic">{donation.message}</p>
                )}
              </div>
            ) : (
              <Link
                href="/donate"
                className="inline-flex items-center gap-2 bg-rose-500/10 border border-rose-500/20 text-rose-400 hover:bg-rose-500/20 font-semibold text-sm px-5 py-2.5 rounded-lg transition-colors"
              >
                <Heart size={14} />
                Donate
              </Link>
            )}
          </div>
        </section>

        {/* Footer links */}
        <section className="flex flex-wrap gap-4 justify-center pt-4 border-t border-card-bg text-sm text-fg-subtle">
          <Link href="/changelog" className="hover:text-sky-400 transition-colors">Changelog</Link>
          <Link href="/faq" className="hover:text-sky-400 transition-colors">FAQ</Link>
          <Link href="/screener" className="hover:text-sky-400 transition-colors">Stock Screener</Link>
          <Link href="/heatmap" className="hover:text-sky-400 transition-colors">Market Heatmap</Link>
        </section>
      </div>
    </div>
  );
}
